import { useState } from "react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Globe,
  Image,
  FileText,
  PenTool,
  Calendar,
  Search,
  Library,
  Building,
  Brain,
  Key,
  ClipboardList,
  Settings,
  Plus,
  Filter,
  Download,
  RefreshCw,
  CheckCircle2,
  Clock,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";

const placeholderModules = [
  {
    title: "Website CMS",
    icon: Globe,
    description: "Manage homepage, about sections, and public content",
  },
  {
    title: "Gallery",
    icon: Image,
    description: "Upload and organize images and videos",
  },
  {
    title: "Books (PDFs)",
    icon: FileText,
    description: "Manage digital book uploads and metadata",
  },
  {
    title: "Blog",
    icon: PenTool,
    description: "Create and publish blog posts",
  },
  {
    title: "Events",
    icon: Calendar,
    description: "Manage community events and registrations",
  },
  {
    title: "Research Tools",
    icon: Search,
    description: "SodhSanchay, SodhSandarbh, Śabdasaṅgraha management",
  },
  {
    title: "Digital Museum",
    icon: Building,
    description: "Muni Jambuvijayaji Research Center exhibits and multimedia",
  },
  {
    title: "AI & Indexing",
    icon: Brain,
    description: "Search index and AI configuration",
  },
  {
    title: "Access Requests",
    icon: Key,
    description: "Manage content access approvals",
  },
  {
    title: "Logs",
    icon: ClipboardList,
    description: "System audit trails and activity logs",
  },
  {
    title: "Settings",
    icon: Settings,
    description: "System configuration and preferences",
  },
];

export function AdminPlaceholder({ title }: { title: string }) {
  const module =
    placeholderModules.find(m => m.title === title) || placeholderModules[0];
  const Icon = module.icon;
  const items: Array<{
    id: string;
    name: string;
    category: string;
    date: string;
    status: string;
  }> = [];

  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = items.filter(
    item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title={title}>
      <div className="space-y-6">
        {/* Module Header Banner */}
        <div className="flex flex-col justify-between gap-4 rounded-2xl bg-gradient-to-r from-umber-900 via-umber-800 to-heritage-950 p-6 text-white shadow-xl sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-heritage-500/30 bg-heritage-500/20 text-heritage-400">
              <Icon className="h-7 w-7" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-heritage-100">
                {title} Management
              </h1>
              <p className="mt-0.5 text-sm text-heritage-200/70">
                {module.description}
              </p>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Button
              variant="outline"
              className="flex-1 justify-center rounded-xl border-heritage-500/40 bg-heritage-950/70 text-heritage-100 hover:border-heritage-400 hover:bg-heritage-900 hover:text-white sm:flex-none"
            >
              <RefreshCw className="mr-2 h-4 w-4 shrink-0" /> Sync
            </Button>
            <Button className="flex-1 justify-center rounded-xl bg-gradient-to-r from-heritage-500 to-heritage-600 font-semibold text-umber-950 shadow-md hover:from-heritage-600 hover:to-heritage-700 sm:flex-none">
              <Plus className="mr-2 h-4 w-4 shrink-0" /> Add New
            </Button>
          </div>
        </div>

        {/* Toolbar & Filter Card */}
        <Card className="shadow-xs rounded-2xl border border-umber-200/80">
          <CardContent className="flex flex-col items-center justify-between gap-4 p-4 sm:flex-row">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-umber-400" />
              <Input
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="rounded-xl border-umber-200 bg-umber-50/50 pl-9 focus:bg-card"
              />
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <Button
                variant="outline"
                className="group rounded-xl border-umber-200 text-xs text-umber-700"
              >
                <Filter className="mr-2 h-3.5 w-3.5 text-umber-500 transition-colors group-hover:text-white" />{" "}
                Filter
              </Button>
              <Button
                variant="outline"
                className="group rounded-xl border-umber-200 text-xs text-umber-700"
              >
                <Download className="mr-2 h-3.5 w-3.5 text-umber-500 transition-colors group-hover:text-white" />{" "}
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Records Table */}
        <Card className="shadow-xs overflow-hidden rounded-2xl border border-umber-200/80">
          <CardHeader className="border-b border-umber-100 bg-umber-50/80 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-base font-semibold text-umber-800">
                {title} Records ({filteredItems.length})
              </CardTitle>
              <Badge
                variant="outline"
                className="border-heritage-200 bg-heritage-50 text-xs text-heritage-800"
              >
                Active Module
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-umber-50/40">
                  <TableHead className="w-[100px]">ID</TableHead>
                  <TableHead>Title / Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date Modified</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-12 text-center text-umber-500"
                    >
                      No records available.
                    </TableCell>
                  </TableRow>
                )}
                {filteredItems.map(item => (
                  <TableRow
                    key={item.id}
                    className="transition-colors hover:bg-heritage-50/30"
                  >
                    <TableCell className="font-mono text-xs font-semibold text-heritage-900">
                      {item.id}
                    </TableCell>
                    <TableCell className="font-medium text-umber-900">
                      {item.name}
                    </TableCell>
                    <TableCell className="text-xs text-umber-600">
                      {item.category}
                    </TableCell>
                    <TableCell className="text-xs text-umber-500">
                      {item.date}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          item.status.includes("Published") ||
                          item.status.includes("Active") ||
                          item.status.includes("Indexed") ||
                          item.status.includes("Verified")
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border border-heritage-200 bg-heritage-50 text-heritage-700"
                        }
                      >
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-umber-500 hover:text-heritage-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-umber-500 hover:text-heritage-700"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-umber-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

// Export individual page components
export const GalleryPage = () => <AdminPlaceholder title="Gallery" />;
export const BooksPage = () => <AdminPlaceholder title="Books (PDFs)" />;
export const ArticlesPage = () => (
  <AdminPlaceholder title="Articles/Tributes" />
);
export const BlogPage = () => <AdminPlaceholder title="Blog" />;
export const EventsPage = () => <AdminPlaceholder title="Events" />;
export const ResearchPage = () => <AdminPlaceholder title="Research Tools" />;
export const MuseumPage = () => <AdminPlaceholder title="Digital Museum" />;
export const AIPage = () => <AdminPlaceholder title="AI & Indexing" />;
export const AccessRequestsPage = () => (
  <AdminPlaceholder title="Access Requests" />
);
export const LogsPage = () => <AdminPlaceholder title="Logs" />;
export const SettingsPage = () => <AdminPlaceholder title="Settings" />;
